import { PlanId } from "components/common/boundary/PlanId";
import {
  filterPlanResultsToPeriod,
  normaliseManualBaskets,
} from "components/common/plan/utils";
import {
  ChildMixUpdate,
  generatedApi,
  LayerSource,
  PlannedResultsRead,
  useUpdatePlanResultsMutation,
} from "src/store/api/generatedApi";
import {
  parsePlanPeriodId,
  PlanPeriodId,
} from "src/store/slices/planMixesTableEditSlice";
import { steelGradeSearchSlice } from "src/store/slices/steelGradeSearchSlice";
import { useAppDispatch } from "src/store/store";
import { typeSafeObjectEntries } from "src/utils/typeSafeObjectEntries";
import { typeSafeObjectKeys } from "src/utils/typeSafeObjectKeys";

// A non-manual save is re-stratified server-side; a manual save is persisted
// verbatim. The layer-editor payload already meets the backend's contract, so
// only the table-merged baskets need normalising.
export const resolveSavedMixUpdate = ({
  tableUpdate,
  layerEditorUpdate,
  isManual,
}: {
  tableUpdate: ChildMixUpdate;
  layerEditorUpdate: ChildMixUpdate | undefined;
  isManual: boolean;
}): ChildMixUpdate => {
  if (!isManual) {
    return { ...tableUpdate, layer_source: "per_basket_totals" };
  }
  if (layerEditorUpdate) {
    return {
      ...layerEditorUpdate,
      parent_mix_ids: tableUpdate.parent_mix_ids,
      layer_source: "manual",
    };
  }
  const inlineMix = tableUpdate.new_mix_production.mix;
  if (!inlineMix) {
    // The manual editor always carries an inline mix; the mix_id-reference form is
    // only used by heat-override deploys, which never flow through this save path.
    return { ...tableUpdate, layer_source: "manual" };
  }
  return {
    ...tableUpdate,
    new_mix_production: {
      ...tableUpdate.new_mix_production,
      mix: {
        ...inlineMix,
        baskets: normaliseManualBaskets(inlineMix.baskets),
      },
    },
    layer_source: "manual",
  };
};

export const makeHandleOnSaveChanges = (
  searchId: number | null,
  planId: PlanId,
  doUpdatePlanResultsMutation: ReturnType<
    typeof useUpdatePlanResultsMutation
  >[0],
  tenantName: string,
  setPlanId: (planId: PlanId) => void,
  getResult: ReturnType<typeof generatedApi.useLazyGetResultQuery>[0],
  dispatch: ReturnType<typeof useAppDispatch>,
  comment: string,
  layerSource: LayerSource
) => {
  return async (
    editedMixes: Record<PlanPeriodId, ChildMixUpdate[]>
  ): Promise<PlannedResultsRead | null> => {
    const { data: updated_plan, error } = await doUpdatePlanResultsMutation({
      tenantName,
      planId: planId,
      planMixUpdates: {
        period_updates: typeSafeObjectEntries(editedMixes)
          .filter(
            ([planPeriodId]) => parsePlanPeriodId(planPeriodId)[0] == planId
          )
          .map(([planPeriodId, updates]) => {
            const period = parsePlanPeriodId(planPeriodId)[1];
            return {
              period,
              updates,
              replacements: {},
            };
          }),
        comment: comment,
        layer_source: layerSource,
      },
    });

    if (error === undefined) {
      typeSafeObjectKeys(editedMixes)
        .filter((planPeriodId) => parsePlanPeriodId(planPeriodId)[0] == planId)
        .forEach((planPeriodId) => {
          const [, period] = parsePlanPeriodId(planPeriodId);
          const updatedPlanId = updated_plan.plan_id;
          void dispatch(
            generatedApi.util.upsertQueryData(
              "getPlanResults",
              { planId: updatedPlanId, tenantName, period: period },
              filterPlanResultsToPeriod(updated_plan, period)
            )
          );

          dispatch(
            steelGradeSearchSlice.actions.copy([
              planId,
              period,
              "deploy",
              updated_plan.plan_id as PlanId,
              period,
            ])
          );
        });

      if (searchId === null) {
        throw new Error(
          `Search id should not be null in context with plan id ${planId}`
        );
      }

      await getResult({ searchId, tenantName });
      setPlanId(updated_plan.plan_id as PlanId);
      return updated_plan;
    }

    return null;
  };
};
